import { CopyOutlined, LinkOutlined } from '@ant-design/icons'
import type { TablePaginationConfig, TableProps } from 'antd'
import { Button, Card, message, Select, Table, Tooltip } from 'antd'
import copy from 'copy-to-clipboard'
import React, { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { HISTORY_TYPE } from 'constants/history-type'
import { logError } from 'utils/logger'

import './style.scss'

export default function TransactionHistory() {
  const additionalItems = [BREADCRUMBS.TRANSACTION_HISTORY]
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [current, setCurrent] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [byType, setByType] = useState<number | undefined>(0)
  const [contractAddress, setContractAddress] = useState<string | undefined>(undefined)
  const [txHash, setTxHash] = useState<string | undefined>(undefined)
  const [ownerAddress, setOwnerAddress] = useState<string | undefined>(undefined)

  const getData = useCallback(
    async (pageIndex = 1, pageSize = 10) => {
      setLoading(true)
      try {
        const res = await AppAdmin.getListTransaction(
          pageSize,
          pageIndex - 1,
          byType,
          contractAddress,
          txHash,
          ownerAddress
        )
        if (res?.data.success) {
          const history = res?.data.data.map((item: any, index: number) => ({
            key: `${pageIndex}-${index}`,
            ownerAddress: item?.ownerAddress,
            amount: item?.amount,
            byType: item?.byType,
            byStatus: item?.byStatus,
            processHash: item?.processHash,
            txHash: item?.txHash,
            assetToken: item?.assetToken,
            contractAddress: item?.contractAddress,
            createdAt: new Date(item?.createdAt).toUTCString(),
          }))
          setData(history)
          setTotal(res?.data.totalCount)
        }
      } catch (error) {
        setData([])
        setTotal(0)
        logError('Error fetching data: ', error)
      } finally {
        setLoading(false)
      }
    },
    [byType, contractAddress, ownerAddress, txHash]
  )

  useEffect(() => {
    getData(current, pageSize)
  }, [current, pageSize, byType, contractAddress, txHash, ownerAddress, getData])

  useEffect(() => {
    setCurrent(1)
    setPageSize(10)
  }, [byType, contractAddress, txHash, ownerAddress])

  const columns: TableProps<any>['columns'] = [
    {
      title: 'User Address',
      dataIndex: 'ownerAddress',
      key: 'ownerAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/address/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'By Type',
      dataIndex: 'byType',
      key: 'byType',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text) => <>{new Intl.NumberFormat('en-US').format(text)}</>,
    },
    {
      title: 'Asset Token',
      dataIndex: 'assetToken',
      key: 'assetToken',
    },
    {
      title: 'By Status',
      dataIndex: 'byStatus',
      key: 'byStatus',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Tx Hash',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/tx/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    // {
    //   title: "Contract Address",
    //   dataIndex: "contractAddress",
    //   key: "contractAddress",
    //   render: (text) => (
    //     <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    //       <a target="_blank" href={`https://basescan.org/address/${text}`}>{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
    //       <Tooltip title="Copy">
    //         <Button
    //           type="link"
    //           icon={<CopyOutlined />}
    //           onClick={() => {
    //             copy(text);
    //             message.success("Copied Success!");
    //           }}
    //         />
    //       </Tooltip>
    //     </div>
    //   ),
    // },
  ]

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || 1)
    setPageSize(newPageSize || 10)
  }

  return (
    <div className="main-section transaction-history">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title" style={{ marginBottom: 20 }}>
        Transaction History
      </div>
      <div className="transaction-search">
        <Select
          placeholder="By type"
          options={[
            { value: HISTORY_TYPE.ALL, label: 'ALL' },
            { value: HISTORY_TYPE.STAKING, label: 'STAKING' },
            { value: HISTORY_TYPE.UN_STAKE, label: 'UNSTAKE' },
            { value: HISTORY_TYPE.STAKE_REWARD, label: 'STAKE REWARD' },
            { value: HISTORY_TYPE.STAKE_CLAIM_REWARD, label: 'STAKE CLAIM REWARD' },
            { value: HISTORY_TYPE.VESTING, label: 'VESTING' },
            { value: HISTORY_TYPE.P2P_DEPOSIT, label: 'P2P DEPOSIT' },
            { value: HISTORY_TYPE.STAKE_WITHDRAW, label: 'STAKE WITHDRAW' },
            { value: HISTORY_TYPE.P2P_WITHDRAW, label: 'P2P WITHDRAW' },
            { value: HISTORY_TYPE.TOKENIZE_DEPOSIT, label: 'TOKENIZE DEPOSIT' },
            { value: HISTORY_TYPE.TOKENIZE_WITHDRAW, label: 'TOKENIZE WITHDRAW' },
            { value: HISTORY_TYPE.TOKENIZE_REFUND, label: 'TOKENIZE REFUND' },
            { value: HISTORY_TYPE.REF_CLAIM_REWARD, label: 'REF CLAIM REWARD' },
          ]}
          value={byType}
          onChange={(value) => setByType(value)}
          style={{ width: 150, textAlign: 'start', height: 38 }}
        />
        <input
          type="text"
          placeholder="Contract Address"
          value={contractAddress}
          onChange={(e) => setContractAddress(e.target.value)}
        />
        <input
          type="text"
          placeholder="Tx Hash"
          value={txHash}
          onChange={(e) => setTxHash(e.target.value)}
        />
        <input
          type="text"
          placeholder="User Address"
          value={ownerAddress}
          onChange={(e) => setOwnerAddress(e.target.value)}
        />
        <Button
          type="primary"
          // onClick={() => getData(current, pageSize)}
          onClick={() => {
            setCurrent(1)
            setPageSize(10)
            getData(1, 10)
          }}
        >
          Search
        </Button>
      </div>
      <Card>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={total < 11 ? false : { total, current, pageSize }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  )
}
